import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Swal from "sweetalert2";
import { FaArrowRight } from "react-icons/fa";
import {
  bulkRecategorizeClientNotes,
  getItemRecordId,
  resolveNoteTabKey,
} from "../../utilis/clientRecategorize";

const TARGET_LABELS = {
  general: "General",
  financial: "Financial",
  finance: "Financial",
};

export function activateNotesTab(tabKey, setActiveNoteTab, tabsId) {
  const resolved = resolveNoteTabKey(tabKey);

  flushSync(() => {
    setActiveNoteTab(resolved);
  });

  if (typeof window !== "undefined") {
    window.requestAnimationFrame(() => {
      const candidates = tabsId
        ? [
            `#${tabsId}-tab-${resolved}`,
            `#${tabsId}-tab-${resolved}-tab`,
            `[data-rr-ui-event-key="${resolved}"]`,
            `#notes-tabs-tab-${resolved}`,
            `#client-notes-tabs-tab-${resolved}`,
          ]
        : [
            `[data-rr-ui-event-key="${resolved}"]`,
            `#notes-tabs-tab-${resolved}`,
            `#client-notes-tabs-tab-${resolved}`,
          ];

      for (const selector of candidates) {
        const tabButton = document.querySelector(selector);
        if (tabButton) {
          tabButton.click();
          break;
        }
      }

      const tabContainer = tabsId
        ? document.getElementById(tabsId)
        : document.getElementById("notes-tabs") ||
          document.getElementById("client-notes-tabs");

      tabContainer
        ?.querySelectorAll('[role="tab"]')
        .forEach((tabEl) => {
          const eventKey = tabEl.getAttribute("data-rr-ui-event-key");
          const isActive =
            eventKey === resolved || tabEl.id.includes(`-tab-${resolved}`);
          tabEl.setAttribute("aria-selected", isActive ? "true" : "false");
          tabEl.classList.toggle("active", isActive);
        });

      tabContainer
        ?.querySelectorAll('[role="tabpanel"]')
        .forEach((panelEl) => {
          const eventKey = panelEl.getAttribute("data-rr-ui-event-key");
          const isActive =
            eventKey === resolved ||
            panelEl.id.includes(`tabpane-${resolved}`) ||
            panelEl.id.includes(`-${resolved}-tabpane`);
          panelEl.classList.toggle("active", isActive);
          panelEl.classList.toggle("show", isActive);
        });
    });
  }

  return resolved;
}

function NotesCategoryMoveToolbar({
  notes = [],
  selectedIds = [],
  onToggleNote,
  onSelectAll,
  onClearSelection,
  clientId,
  currentCategory,
  onSuccess,
  onTabChange,
  setActiveNoteTab,
  tabsId,
  appId,
  appType,
  enabled = true,
  embedded = false,
}) {
  const [loading, setLoading] = useState(false);

  const noteIds = useMemo(
    () =>
      notes
        .map((note) => String(getItemRecordId(note)))
        .filter((noteId) => noteId && noteId !== "null"),
    [notes],
  );

  const allSelected =
    noteIds.length > 0 && noteIds.every((noteId) => selectedIds.includes(noteId));

  const selectedCount = useMemo(
    () => selectedIds.filter((id) => noteIds.includes(String(id))).length,
    [selectedIds, noteIds],
  );

  const someSelected = selectedCount > 0 && !allSelected;
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const targetCategory =
    currentCategory === "financial" || currentCategory === "finance"
      ? "general"
      : "financial";
  const targetLabel = TARGET_LABELS[targetCategory] || targetCategory;

  if (!enabled || notes.length === 0) return null;

  const handleMoveSelected = async () => {
    if (!selectedIds.length) {
      Swal.fire({
        title: "Select notes",
        text: "Choose one or more notes to move.",
        icon: "info",
        timer: 1800,
        showConfirmButton: false,
      });
      return;
    }

    const selectedNotes = notes.filter((note) =>
      selectedIds.includes(String(getItemRecordId(note))),
    );

    const confirmed = await Swal.fire({
      title: "Move selected notes?",
      text: `Move ${selectedNotes.length} note${
        selectedNotes.length === 1 ? "" : "s"
      } to ${targetLabel}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Move to ${targetLabel}`,
      cancelButtonText: "Cancel",
    });

    if (!confirmed.isConfirmed) return;

    setLoading(true);
    try {
      await bulkRecategorizeClientNotes({
        notes: selectedNotes,
        clientId,
        currentCategory,
        category: targetCategory,
        appId,
        appType,
      });

      onClearSelection?.();

      const switchTab = () => {
        if (typeof setActiveNoteTab === "function") {
          activateNotesTab(targetCategory, setActiveNoteTab, tabsId);
        } else {
          onTabChange?.(targetCategory);
        }
      };

      switchTab();

      requestAnimationFrame(() => {
        document
          .getElementById("scroll")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });

      if (onSuccess) {
        void Promise.resolve(onSuccess(targetCategory));
      }

      await Swal.fire({
        title: "Updated",
        text: `${selectedNotes.length} note${
          selectedNotes.length === 1 ? "" : "s"
        } moved to ${targetLabel}.`,
        icon: "success",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message ||
          error.message ||
          "Could not move selected notes.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const moveButtonClassName = embedded
    ? "notes-category-toolbar__move-btn notes-category-toolbar__move-btn--embedded cms-commission-notes-move-btn inline-flex items-center justify-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-[12px] font-semibold text-white shadow-sm transition hover:bg-[#0069d9] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-white"
    : "btn btn-primary btn-sm notes-category-toolbar__move-btn d-inline-flex align-items-center gap-1";

  return (
    <div
      className={`notes-category-toolbar${
        embedded ? " notes-category-toolbar--embedded" : ""
      }`}
    >
      <div className="notes-category-toolbar__main">
        <div className="notes-category-toolbar__select-wrap">
          <label className="notes-category-toolbar__select-control">
            <span className="notes-category-toolbar__select-column">
              <input
                ref={selectAllRef}
                type="checkbox"
                className="form-check-input notes-category-toolbar__checkbox"
                checked={allSelected}
                onChange={() =>
                  allSelected ? onClearSelection?.() : onSelectAll?.(noteIds)
                }
                disabled={loading}
              />
            </span>
            <span className="notes-category-toolbar__select-text">
              {allSelected ? "Deselect all" : "Select all"}
            </span>
          </label>

          <span
            className={`notes-category-toolbar__count${
              selectedCount > 0 ? " is-active" : ""
            }`}
            aria-live="polite"
          >
            {selectedCount > 0
              ? `${selectedCount} of ${notes.length} selected`
              : `${notes.length} note${notes.length === 1 ? "" : "s"}`}
          </span>
        </div>

        <button
          type="button"
          className={moveButtonClassName}
          style={embedded ? { border: "none" } : undefined}
          onClick={handleMoveSelected}
          disabled={loading || selectedIds.length === 0}
        >
          {loading ? (
            "Moving..."
          ) : (
            <>
              <span className="notes-category-toolbar__move-label">
                Move to {targetLabel}
              </span>
              <FaArrowRight
                size={10}
                className="notes-category-toolbar__move-icon"
                aria-hidden
              />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function NoteCategorySelectCheckbox({
  noteId,
  selected,
  onToggle,
  disabled = false,
}) {
  if (!noteId) return null;

  return (
    <div className="note-select-cell">
      <input
        type="checkbox"
        className="form-check-input note-select-checkbox"
        checked={selected}
        onChange={() => onToggle?.(String(noteId))}
        disabled={disabled}
        aria-label="Select note"
      />
    </div>
  );
}

export default NotesCategoryMoveToolbar;
